/**
 * OperationalLineageEdgeRenderer
 *
 * Deterministic SVG edge rendering for each LineageEdgeType.
 * Visual weight communicates semantic certainty:
 *   verified_by / signed_by → stronger stroke
 *   derived_from → thinner lineage
 *   replayed_as → dashed
 *   continuity_transition → animated directional
 */

import React from 'react';
import type { LineageEdge, LineageEdgeType } from '../operationalLineageTypes.js';
import { NEUTRAL, SEMANTIC } from '../../../modules/governanceEventStream/operationalVisualLanguage/index.js';
import { TRANSITION_GRAPH_EDGE } from '../../motion/motionTokens.js';
import type { LayoutNode } from './OperationalLineageNodeRenderer.js';

export interface LayoutEdge extends LineageEdge {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}

function getEdgeStyle(type: LineageEdgeType): { stroke: string; strokeWidth: number; dashArray: string; opacity: number; animated: boolean } {
  switch (type) {
    case 'produced':
      return { stroke: SEMANTIC.informational.marker, strokeWidth: 1, dashArray: 'none', opacity: 0.6, animated: false };
    case 'derived_from':
      return { stroke: NEUTRAL.text300, strokeWidth: 1, dashArray: 'none', opacity: 0.4, animated: false };
    case 'rendered_as':
      return { stroke: NEUTRAL.text300, strokeWidth: 1, dashArray: 'none', opacity: 0.5, animated: false };
    case 'verified_by':
      return { stroke: SEMANTIC.stable.marker, strokeWidth: 2, dashArray: 'none', opacity: 0.8, animated: false };
    case 'signed_by':
      return { stroke: SEMANTIC.admissible.marker, strokeWidth: 2, dashArray: 'none', opacity: 0.9, animated: false };
    case 'replayed_as':
      return { stroke: SEMANTIC.replayWindow.marker, strokeWidth: 1, dashArray: '4,3', opacity: 0.6, animated: false };
    case 'continuity_transition':
      return { stroke: SEMANTIC.degraded.marker, strokeWidth: 1.5, dashArray: '6,3', opacity: 0.7, animated: true };
    case 'hash_bound_to':
      return { stroke: NEUTRAL.text200, strokeWidth: 1, dashArray: '2,2', opacity: 0.4, animated: false };
    default:
      return { stroke: NEUTRAL.text300, strokeWidth: 1, dashArray: 'none', opacity: 0.5, animated: false };
  }
}

function computeBezierPath(edge: LayoutEdge): string {
  const dx = edge.targetX - edge.sourceX;
  const dy = edge.targetY - edge.sourceY;
  const cp1x = edge.sourceX + dx * 0.5;
  const cp1y = edge.sourceY;
  const cp2x = edge.targetX - dx * 0.5;
  const cp2y = edge.targetY;
  return `M ${edge.sourceX} ${edge.sourceY} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${edge.targetX} ${edge.targetY}`;
}

interface EdgeRendererProps {
  edge: LayoutEdge;
  isSelected: boolean;
}

export const OperationalLineageEdgeRenderer: React.FC<EdgeRendererProps> = ({ edge, isSelected }) => {
  const style = getEdgeStyle(edge.type);
  const path = computeBezierPath(edge);
  const stroke = isSelected ? '#d8d8e8' : style.stroke;
  const strokeWidth = isSelected ? style.strokeWidth + 1 : style.strokeWidth;

  return (
    <g data-testid={`lineage-graph-edge-${edge.type}`}>
      <path
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={style.dashArray === 'none' ? undefined : style.dashArray}
        opacity={style.opacity}
        style={{ transition: TRANSITION_GRAPH_EDGE }}
      />
      {/* Animated dash offset for continuity transitions */}
      {style.animated && (
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray="6,3"
          opacity={style.opacity * 1.2}
          style={{
            animation: 'lineageContinuityFlow 1200ms linear infinite',
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Directional arrow head for verified_by, signed_by, continuity_transition */}
      {(edge.type === 'verified_by' || edge.type === 'signed_by' || edge.type === 'continuity_transition') && (
        <polygon
          points={`${edge.targetX},${edge.targetY - 4} ${edge.targetX - 4},${edge.targetY + 4} ${edge.targetX + 4},${edge.targetY + 4}`}
          fill={stroke}
          opacity={style.opacity}
          transform={`rotate(${Math.atan2(edge.targetY - edge.sourceY, edge.targetX - edge.sourceX) * (180 / Math.PI) + 90} ${edge.targetX} ${edge.targetY})`}
          style={{ pointerEvents: 'none' }}
        />
      )}
    </g>
  );
};

/**
 * Augment raw edges with source/target layout positions.
 */
export function layoutEdges(edges: LineageEdge[], layoutNodes: LayoutNode[]): LayoutEdge[] {
  const posMap = new Map<string, { x: number; y: number }>();
  for (const n of layoutNodes) {
    posMap.set(n.id, { x: n.x, y: n.y });
  }

  return edges
    .map((edge) => {
      const src = posMap.get(edge.source);
      const tgt = posMap.get(edge.target);
      if (!src || !tgt) return null;
      return {
        ...edge,
        sourceX: src.x,
        sourceY: src.y + 16, // bottom center of node
        targetX: tgt.x,
        targetY: tgt.y - 16, // top center of node
      };
    })
    .filter((e): e is LayoutEdge => e !== null);
}
